import { useEffect, useState } from "react";
import {
  ArrowLeft,
  CheckCircle,
  XCircle,
  RefreshCw,
  Database,
  Calendar,
  AlertCircle,
  Zap,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../../services/api";

function WebexSettings() {
  const navigate = useNavigate();
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isUpdatingMetadata, setIsUpdatingMetadata] = useState(false);
  const [metadataUpdateDays, setMetadataUpdateDays] = useState(7);

  // Fetch Webex status on mount
  useEffect(() => {
    fetchWebexStatus();
  }, []);

  const fetchWebexStatus = async () => {
    try {
      setIsLoading(true);
      const response = await api.getWebexStatus();
      setIsConnected(response.connected);
      setStatus(response);
    } catch (error) {
      console.error("Failed to fetch Webex status:", error);
      toast.error("Failed to load Webex integration status");
    } finally {
      setIsLoading(false);
    }
  };

  const handleConnect = async () => {
    try {
      const response = await api.connectWebex();
      window.location.href = response.authUrl;
    } catch (error) {
      console.error("Failed to connect Webex:", error);
      toast.error("Failed to connect to Webex");
    }
  };

  const handleDisconnect = async () => {
    if (
      window.confirm(
        "Are you sure you want to disconnect Webex? This will remove all webhook configurations.",
      )
    ) {
      try {
        await api.disconnectWebex();
        setIsConnected(false);
        setStatus(null);
        toast.success("Webex disconnected successfully");
        navigate("/settings?tab=integrations");
      } catch (error) {
        console.error("Failed to disconnect Webex:", error);
        toast.error("Failed to disconnect Webex");
      }
    }
  };

  const handleSyncRecordings = async () => {
    try {
      setIsSyncing(true);
      await api.syncWebexRecordings({
        from: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
        to: new Date().toISOString(),
      });
      toast.success("Recording sync started. This may take a few minutes.");
    } catch (error) {
      console.error("Failed to sync recordings:", error);
      toast.error("Failed to start recording sync");
    } finally {
      setIsSyncing(false);
    }
  };

  const handleUpdateMetadata = async () => {
    if (!metadataUpdateDays || metadataUpdateDays < 1) {
      toast.error("Please enter a valid number of days");
      return;
    }

    try {
      setIsUpdatingMetadata(true);
      await api.updateWebexMetadata({ daysBack: parseInt(metadataUpdateDays) });
      toast.success(
        `Metadata update started for recordings from the last ${metadataUpdateDays} days. Check your logs for progress.`,
        { autoClose: 5000 },
      );
    } catch (error) {
      console.error("Failed to update metadata:", error);
      toast.error(error.response?.data?.message || "Failed to update metadata");
    } finally {
      setIsUpdatingMetadata(false);
    }
  };

  const handleRecreateWebhook = async () => {
    if (
      window.confirm(
        "Recreate the Webex webhook? This will delete the old webhook and create a new one.",
      )
    ) {
      try {
        await api.recreateWebexWebhook();
        toast.success("Webhook recreated successfully");
        fetchWebexStatus(); // Refresh status
      } catch (error) {
        console.error("Failed to recreate webhook:", error);
        toast.error("Failed to recreate webhook");
      }
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#0C0E12] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="text-center py-12">
            <RefreshCw className="h-8 w-8 text-purple-500 animate-spin mx-auto mb-4" />
            <p className="text-gray-400">Loading Webex integration...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0C0E12] text-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate("/settings?tab=integrations")}
            className="flex items-center text-gray-400 hover:text-white mb-4 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Integrations
          </button>
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-white rounded-lg flex items-center justify-center">
              <img
                src="/assets/cisco.png"
                alt="Webex logo"
                className="w-12 h-12 object-contain"
              />
            </div>
            <div>
              <h1 className="text-2xl font-semibold">Webex Integration</h1>
              <p className="text-gray-400 text-sm mt-1">
                Manage your Webex calling integration and recording sync
              </p>
            </div>
          </div>
        </div>

        {/* Connection Status */}
        <div className="bg-[#1a1d23] border border-gray-700 rounded-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium">Connection Status</h2>
            {isConnected ? (
              <div className="flex items-center text-green-400">
                <CheckCircle className="h-5 w-5 mr-2" />
                Connected
              </div>
            ) : (
              <div className="flex items-center text-gray-400">
                <XCircle className="h-5 w-5 mr-2" />
                Not Connected
              </div>
            )}
          </div>

          {isConnected && status ? (
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-400">Webhook Status:</span>
                <span
                  className={`${
                    status.credential?.webhookConfigured
                      ? "text-green-400"
                      : "text-yellow-400"
                  }`}
                >
                  {status.credential?.webhookConfigured
                    ? "Configured"
                    : "Not Configured"}
                </span>
              </div>
              {status.credential?.webhookUrl && (
                <div className="flex justify-between">
                  <span className="text-gray-400">Webhook URL:</span>
                  <span className="text-white text-xs font-mono break-all">
                    {status.credential.webhookUrl}
                  </span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-gray-400">Token Status:</span>
                <span
                  className={`${
                    !status.credential?.tokenExpired
                      ? "text-green-400"
                      : "text-red-400"
                  }`}
                >
                  {!status.credential?.tokenExpired ? "Valid" : "Expired"}
                </span>
              </div>
            </div>
          ) : (
            <p className="text-gray-400 text-sm">
              Connect your Webex account to start syncing call recordings
              automatically.
            </p>
          )}

          <div className="mt-6 flex gap-3">
            {isConnected ? (
              <>
                <button
                  onClick={handleDisconnect}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                >
                  Disconnect
                </button>
                {status?.credential?.webhookConfigured && (
                  <button
                    onClick={handleRecreateWebhook}
                    className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                  >
                    Recreate Webhook
                  </button>
                )}
              </>
            ) : (
              <button
                onClick={handleConnect}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
              >
                Connect Webex
              </button>
            )}
          </div>
        </div>

        {/* Actions - Only show if connected */}
        {isConnected && (
          <>
            {/* Sync Recordings */}
            <div className="bg-[#1a1d23] border border-gray-700 rounded-lg p-6 mb-6">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-purple-600/20 rounded-lg">
                  <RefreshCw className="h-6 w-6 text-purple-400" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-medium mb-2">Sync Recordings</h3>
                  <p className="text-gray-400 text-sm mb-4">
                    Manually sync call recordings from the last 7 days. This
                    will fetch recordings and process them for transcription and
                    analysis.
                  </p>
                  <button
                    onClick={handleSyncRecordings}
                    disabled={isSyncing}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                      isSyncing
                        ? "bg-gray-700 text-gray-400 cursor-not-allowed"
                        : "bg-purple-600 hover:bg-purple-700 text-white"
                    }`}
                  >
                    {isSyncing ? (
                      <>
                        <RefreshCw className="h-4 w-4 animate-spin" />
                        Syncing...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="h-4 w-4" />
                        Sync Now
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Update Metadata - NEW SECTION */}
            <div className="bg-[#1a1d23] border border-gray-700 rounded-lg p-6 mb-6">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-blue-600/20 rounded-lg">
                  <Database className="h-6 w-6 text-blue-400" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-medium mb-2">
                    Update Call Metadata
                  </h3>
                  <p className="text-gray-400 text-sm mb-4">
                    Update existing call recordings with metadata to improve
                    phone number matching. This extracts the actual connected
                    party phone number from Webex metadata instead of using the
                    recording title.
                  </p>

                  <div className="bg-blue-900/20 border border-blue-800/30 rounded-lg p-4 mb-4">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="h-5 w-5 text-blue-400 flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-blue-200">
                        <p className="font-medium mb-1">Why update metadata?</p>
                        <p className="text-blue-300">
                          Previously, calls were matched using the recording
                          title (e.g., "Call with TM-2"). Updating metadata
                          extracts the actual phone number for accurate lead
                          system matching.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 mb-4">
                    <label className="text-sm text-gray-400">
                      Update recordings from the last:
                    </label>
                    <select
                      value={metadataUpdateDays}
                      onChange={(e) => setMetadataUpdateDays(e.target.value)}
                      className="bg-[#0C0E12] border border-gray-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-purple-500"
                      disabled={isUpdatingMetadata}
                    >
                      <option value="1">1 day</option>
                      <option value="3">3 days</option>
                      <option value="7">7 days</option>
                      <option value="14">14 days</option>
                      <option value="30">30 days</option>
                    </select>
                  </div>

                  <button
                    onClick={handleUpdateMetadata}
                    disabled={isUpdatingMetadata}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                      isUpdatingMetadata
                        ? "bg-gray-700 text-gray-400 cursor-not-allowed"
                        : "bg-blue-600 hover:bg-blue-700 text-white"
                    }`}
                  >
                    {isUpdatingMetadata ? (
                      <>
                        <RefreshCw className="h-4 w-4 animate-spin" />
                        Updating Metadata...
                      </>
                    ) : (
                      <>
                        <Database className="h-4 w-4" />
                        Update Metadata
                      </>
                    )}
                  </button>

                  <p className="text-xs text-gray-500 mt-3">
                    This process runs in the background and may take several
                    minutes. Check your application logs for progress updates.
                  </p>
                </div>
              </div>
            </div>

            {/* Info Section */}
            <div className="bg-[#1a1d23] border border-gray-700 rounded-lg p-6">
              <h3 className="text-lg font-medium mb-4">How it works</h3>
              <div className="space-y-3 text-sm text-gray-400">
                <div className="flex items-start gap-3">
                  <Zap className="h-5 w-5 text-purple-400 flex-shrink-0 mt-0.5" />
                  <p>
                    <span className="text-white font-medium">
                      Real-time Sync:
                    </span>{" "}
                    New recordings are automatically synced via webhooks as
                    they're created
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <Calendar className="h-5 w-5 text-purple-400 flex-shrink-0 mt-0.5" />
                  <p>
                    <span className="text-white font-medium">Manual Sync:</span>{" "}
                    Use the sync button above to fetch recent recordings
                    on-demand
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <Database className="h-5 w-5 text-purple-400 flex-shrink-0 mt-0.5" />
                  <p>
                    <span className="text-white font-medium">
                      Metadata Update:
                    </span>{" "}
                    Enriches existing recordings with actual phone numbers for
                    better lead matching
                  </p>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default WebexSettings;
